import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Drawer,
  Input,
  Button,
  Space,
  Typography,
  Spin,
  Card,
  Tag,
  Divider,
} from "antd";
import { useNavigate } from "react-router-dom";
import OperationRequestSuccessContent from "./components/OperationRequestSuccessContent.jsx";
import { useAgentEntitlements } from "../agentEntitlements/useAgentEntitlements";
import { resolveOrderAmountDisplayForPicker } from "../../utils/orderAmountDisplayResolver";
import {
  searchAgentOrdersForOperationRequest,
  MIN_OPERATION_REQUEST_QUERY_CHARS,
} from "./searchAgentOrdersForOperationRequest.js";
import {
  fetchRecentOrdersForOperationRequest,
  RECENT_OPERATION_REQUEST_LIMIT,
} from "./fetchRecentOrdersForOperationRequest.js";
import {
  pickerOrderDisplayNumber,
  resolvePickerOrderDisplayNumber,
} from "./pickerOrderDisplayResolver.js";
import {
  QUICK_REQUEST_TYPE_BLOCKS,
  NEW_OPERATION_REQUEST_PATH,
  OPERATION_REQUEST_MODES,
  isOrderOptionalRequestType,
  resolveGeneralRequestType,
} from "./operationRequestConstants.js";
import { buildLdsOrdersGuardCtx } from "./operationRequestLdsGuard.js";
import { navigateToCrmOpsDocket } from "../../Pages/Agent/Crm/navigateToCrmOpsDocket";

const { Text } = Typography;

/**
 * Drawer launcher בלבד: מצב, סוג פנייה מהיר, הזמנות אחרונות וחיפוש → מעבר לעמוד מלא.
 */
export default function AgentOperationRequestModal({
  open,
  onClose,
  initialMode,
  successPayload = null,
}) {
  const navigate = useNavigate();

  const successDisplayNumber =
    successPayload?.displayOrderNumber ??
    successPayload?.request?.displayOrderNumber ??
    null;

  const isSuccessView = Boolean(open && successPayload);

  useEffect(() => {
    if (open && successPayload) {
      console.log("[OP_REQUEST_SUCCESS_DRAWER_ONLY]", {
        requestId: successPayload?.request?.id ?? null,
        displayOrderNumber: successDisplayNumber,
      });
    }
  }, [open, successPayload, successDisplayNumber]);

  const handleGoToMyRequests = () => {
    onClose?.();
    const req = successPayload?.request;
    if (req) {
      void navigateToCrmOpsDocket(navigate, req);
      return;
    }
    navigate("/p/dashboard/drafts?tab=dockets&pane=ops");
  };

  const handleCloseAfterSuccess = () => {
    onClose?.();
  };

  const agentEntitlements = useAgentEntitlements();
  const ldsOrdersGuardCtx = useMemo(
    () => buildLdsOrdersGuardCtx(agentEntitlements),
    [
      agentEntitlements.canSearchFlights,
      agentEntitlements.canSearchPackages,
      agentEntitlements.canUseDynamicPackages,
      agentEntitlements.canOrganizedTours,
      agentEntitlements.canSearchHotels,
      agentEntitlements.status,
    ]
  );

  const [pickIntent, setPickIntent] = useState(OPERATION_REQUEST_MODES.operation_request);
  const [pickSubstep, setPickSubstep] = useState("op_quick_type");
  const [requestType, setRequestType] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [pickResults, setPickResults] = useState([]);
  const [pickSearching, setPickSearching] = useState(false);

  const [recentOrders, setRecentOrders] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);

  const pickNormalizedCacheRef = useRef(null);
  const pickSearchSeqRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput;
      setDebouncedQuery(next);
      const tq = String(next ?? "").trim();
      console.log("[OP_REQUEST_SEARCH_QUERY_CHANGED]", {
        length: tq.length,
        query: tq,
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!open) {
      setPickIntent(OPERATION_REQUEST_MODES.operation_request);
      setPickSubstep("op_quick_type");
      setRequestType(null);
      setSearchInput("");
      setDebouncedQuery("");
      setPickResults([]);
      setPickSearching(false);
      setRecentOrders([]);
      setRecentLoading(false);
      pickNormalizedCacheRef.current = null;
      pickSearchSeqRef.current += 1;
      return;
    }
    if (open && successPayload) {
      return;
    }
    const mode =
      initialMode === OPERATION_REQUEST_MODES.payment_link
        ? OPERATION_REQUEST_MODES.payment_link
        : OPERATION_REQUEST_MODES.operation_request;
    setPickIntent(mode);
    setPickSubstep(mode === OPERATION_REQUEST_MODES.payment_link ? "op_pick_order" : "op_quick_type");
    console.log("[OP_REQUEST_SEARCH_IDLE]", { ts: Date.now(), initialMode: mode });
  }, [open, initialMode, successPayload]);

  useEffect(() => {
    if (pickIntent === OPERATION_REQUEST_MODES.payment_link) {
      setPickSubstep("op_pick_order");
    } else if (pickSubstep !== "op_pick_order") {
      setPickSubstep("op_quick_type");
    }
  }, [pickIntent]);

  const allowOrderPickUi =
    !isSuccessView &&
    open &&
    (pickIntent === OPERATION_REQUEST_MODES.payment_link ||
      (pickIntent === OPERATION_REQUEST_MODES.operation_request && pickSubstep === "op_pick_order"));

  useEffect(() => {
    if (!allowOrderPickUi) return;
    let cancelled = false;
    (async () => {
      console.log("[OP_REQUEST_RECENT_ORDERS_LOAD_START]", {
        limit: RECENT_OPERATION_REQUEST_LIMIT,
      });
      setRecentLoading(true);
      try {
        const res = await fetchRecentOrdersForOperationRequest({
          limit: RECENT_OPERATION_REQUEST_LIMIT,
        });
        if (cancelled) return;
        const list = Array.isArray(res.orders) ? res.orders : [];
        setRecentOrders(list);
        console.log("[OP_REQUEST_RECENT_ORDERS_LOADED]", {
          count: list.length,
          meta: res.meta ?? null,
        });
      } catch (e) {
        if (!cancelled) {
          setRecentOrders([]);
          console.warn("[OP_REQUEST_RECENT_ORDERS_LOAD_FAILED]", {
            message: String(e?.message || e),
          });
        }
      } finally {
        if (!cancelled) setRecentLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowOrderPickUi]);

  useEffect(() => {
    if (!allowOrderPickUi) return;

    const q = String(debouncedQuery ?? "").trim();

    if (q.length === 0) {
      setPickResults([]);
      setPickSearching(false);
      return;
    }

    if (q.length < MIN_OPERATION_REQUEST_QUERY_CHARS) {
      setPickResults([]);
      setPickSearching(false);
      return;
    }

    const seq = ++pickSearchSeqRef.current;
    setPickSearching(true);
    setPickResults([]);
    console.log("[OP_REQUEST_SEARCH_STARTED]", { query: q });

    (async () => {
      try {
        const res = await searchAgentOrdersForOperationRequest(q, ldsOrdersGuardCtx, {
          preloadedNormalized: pickNormalizedCacheRef.current,
        });
        if (seq !== pickSearchSeqRef.current) return;

        if (res.normalizedSnapshot != null) {
          pickNormalizedCacheRef.current = res.normalizedSnapshot;
        }

        const list = Array.isArray(res.orders) ? res.orders : [];
        setPickResults(list);

        if (list.length === 0) {
          console.log("[OP_REQUEST_SEARCH_EMPTY]", { query: q, skipped: res.skipped === true });
        } else {
          console.log("[OP_REQUEST_SEARCH_RESULTS]", { query: q, count: list.length });
        }
      } catch (e) {
        if (seq !== pickSearchSeqRef.current) return;
        setPickResults([]);
        console.log("[OP_REQUEST_SEARCH_EMPTY]", {
          query: q,
          error: String(e?.message || e),
        });
      } finally {
        if (seq === pickSearchSeqRef.current) {
          setPickSearching(false);
        }
      }
    })();
  }, [allowOrderPickUi, debouncedQuery, ldsOrdersGuardCtx]);

  const navigateToFullPage = useCallback(
    (order, source, overrideRequestType) => {
      const mode = pickIntent;
      const resolvedType =
        mode === OPERATION_REQUEST_MODES.operation_request
          ? overrideRequestType !== undefined
            ? overrideRequestType
            : requestType
          : null;
      const displayResolved = order ? resolvePickerOrderDisplayNumber(order) : null;
      if (displayResolved) {
        console.log("[OP_REQUEST_SELECTED_ORDER_DISPLAY_NUMBER]", {
          displayNumber: displayResolved.displayNumber,
          orderId: displayResolved.internalOrderId ?? displayResolved.orderId,
          source: displayResolved.source,
        });
        console.log("[OP_REQUEST_ORDER_SELECTED]", {
          orderNumber: displayResolved.displayNumber,
          orderId: displayResolved.internalOrderId ?? order?.orderId ?? null,
          category: order?.category ?? null,
          key: order?.key ?? null,
          source,
          pickIntent: mode,
          requestType: resolvedType,
        });
      }

      const navState = {
        mode,
        requestType: resolvedType,
        selectedOrder: order || null,
      };

      console.log("[OP_REQUEST_NAVIGATE_TO_FULL_PAGE]", {
        mode,
        requestType: navState.requestType,
        orderId: order?.id ?? order?.orderId ?? null,
        withoutOrder: !order,
        path: NEW_OPERATION_REQUEST_PATH,
        source,
      });

      onClose?.();
      navigate(NEW_OPERATION_REQUEST_PATH, { state: navState });
    },
    [pickIntent, requestType, navigate, onClose]
  );

  const handleContinueWithoutOrder = useCallback(() => {
    const type = resolveGeneralRequestType(requestType);
    if (!isOrderOptionalRequestType(type)) return;
    console.log("[OP_REQUEST_CONTINUE_WITHOUT_ORDER]", { requestType: type });
    navigateToFullPage(null, "no_order", type);
  }, [navigateToFullPage, requestType]);

  /** CTA בולט במודאל — פנייה כללית ללא הזמנה, עם סוג נבחר או בירור כללי */
  const handleOpenGeneralRequest = useCallback(() => {
    const type = resolveGeneralRequestType(requestType);
    console.log("[OP_REQUEST_OPEN_GENERAL]", { requestType: type, hadSelection: Boolean(requestType) });
    setPickIntent(OPERATION_REQUEST_MODES.operation_request);
    setRequestType(type);
    const navState = {
      mode: OPERATION_REQUEST_MODES.operation_request,
      requestType: type,
      selectedOrder: null,
    };
    console.log("[OP_REQUEST_NAVIGATE_TO_FULL_PAGE]", {
      mode: navState.mode,
      requestType: type,
      orderId: null,
      withoutOrder: true,
      path: NEW_OPERATION_REQUEST_PATH,
      source: "general_cta",
    });
    onClose?.();
    navigate(NEW_OPERATION_REQUEST_PATH, { state: navState });
  }, [navigate, onClose, requestType]);

  const handleSelectFromSearch = useCallback(
    (order) => {
      navigateToFullPage(order, "search");
    },
    [navigateToFullPage]
  );

  const handleSelectFromRecent = useCallback(
    (order) => {
      console.log("[OP_REQUEST_RECENT_ORDER_SELECTED]", {
        orderNumber: order?.orderId ?? null,
        key: order?.key ?? null,
        pickIntent,
      });
      navigateToFullPage(order, "recent");
    },
    [navigateToFullPage, pickIntent]
  );

  const handleQuickTypeSelect = useCallback((value, label) => {
    setRequestType(value);
    setPickSubstep("op_pick_order");
    console.log("[OP_REQUEST_QUICK_TYPE_SELECTED]", { requestType: value, label });
  }, []);

  const handleChangeQuickType = useCallback(() => {
    setPickSubstep("op_quick_type");
    setSearchInput("");
    setDebouncedQuery("");
    pickSearchSeqRef.current += 1;
    setPickResults([]);
    setPickSearching(false);
  }, []);

  const drawerTitle = useMemo(() => {
    if (pickIntent === OPERATION_REQUEST_MODES.operation_request && pickSubstep === "op_quick_type") {
      return "בחר סוג פנייה";
    }
    if (pickIntent === OPERATION_REQUEST_MODES.payment_link) {
      return "בחר הזמנה ליצירת לינק תשלום";
    }
    return "בחר הזמנה לפנייה לאופרציה";
  }, [pickIntent, pickSubstep]);

  const debouncedTrim = String(debouncedQuery ?? "").trim();
  const inputTrim = String(searchInput ?? "").trim();
  const showSearchIdle =
    allowOrderPickUi && debouncedTrim.length === 0 && inputTrim.length === 0;
  const showSearchTooShort =
    allowOrderPickUi &&
    debouncedTrim.length > 0 &&
    debouncedTrim.length < MIN_OPERATION_REQUEST_QUERY_CHARS &&
    !pickSearching;
  const showSearchEmptyResults =
    allowOrderPickUi &&
    debouncedTrim.length >= MIN_OPERATION_REQUEST_QUERY_CHARS &&
    !pickSearching &&
    pickResults.length === 0;
  const showSearchResults = allowOrderPickUi && pickResults.length > 0;

  const quickTypeLabel =
    QUICK_REQUEST_TYPE_BLOCKS.find((b) => b.value === requestType)?.label ?? null;

  if (isSuccessView) {
    return (
      <Drawer
        title="הפנייה נשלחה"
        placement="left"
        width={Math.min(520, typeof window !== "undefined" ? window.innerWidth - 24 : 520)}
        open={open}
        onClose={onClose}
        destroyOnClose
        styles={{ body: { paddingTop: 12 } }}
      >
        <OperationRequestSuccessContent
          displayOrderNumber={successDisplayNumber}
          onGoToMyRequests={handleGoToMyRequests}
          onClose={handleCloseAfterSuccess}
        />
      </Drawer>
    );
  }

  return (
    <Drawer
      title={drawerTitle}
      placement="left"
      width={Math.min(520, typeof window !== "undefined" ? window.innerWidth - 24 : 520)}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{ body: { paddingTop: 12 } }}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Button
            type={pickIntent === OPERATION_REQUEST_MODES.operation_request ? "primary" : "default"}
            block
            onClick={() => setPickIntent(OPERATION_REQUEST_MODES.operation_request)}
          >
            פתח פנייה לאופרציה
          </Button>
          <Button
            type="default"
            block
            onClick={handleOpenGeneralRequest}
            style={{
              borderColor: "#fa8c16",
              color: "#d46b08",
              fontWeight: 600,
            }}
          >
            פתח פנייה כללית
          </Button>
          <Button
            type={pickIntent === OPERATION_REQUEST_MODES.payment_link ? "primary" : "default"}
            block
            onClick={() => setPickIntent(OPERATION_REQUEST_MODES.payment_link)}
          >
            צור לינק לתשלום
          </Button>
        </Space>

        {pickIntent === OPERATION_REQUEST_MODES.operation_request && pickSubstep === "op_quick_type" ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))",
              gap: 10,
            }}
          >
            {QUICK_REQUEST_TYPE_BLOCKS.map((b) => {
              const highlighted = requestType === b.value;
              return (
                <Card
                  key={b.value}
                  size="small"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleQuickTypeSelect(b.value, b.label);
                    }
                  }}
                  styles={{ body: { padding: 12 } }}
                  style={{
                    cursor: "pointer",
                    borderColor: highlighted ? "#1677ff" : undefined,
                    borderWidth: highlighted ? 2 : 1,
                    boxShadow: highlighted ? "0 0 0 1px rgba(22,119,255,0.25)" : undefined,
                  }}
                  onClick={() => handleQuickTypeSelect(b.value, b.label)}
                >
                  <Text strong style={{ display: "block", textAlign: "center", lineHeight: 1.35 }}>
                    {b.label}
                  </Text>
                </Card>
              );
            })}
          </div>
        ) : allowOrderPickUi ? (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {pickIntent === OPERATION_REQUEST_MODES.operation_request ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Text strong style={{ fontSize: 15 }}>
                  בחר הזמנה רלוונטית
                </Text>
                {quickTypeLabel ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Text type="secondary">סוג פנייה: {quickTypeLabel}</Text>
                    <Button
                      type="link"
                      size="small"
                      onClick={handleChangeQuickType}
                      style={{ padding: 0, height: "auto" }}
                    >
                      שנה סוג פנייה
                    </Button>
                  </div>
                ) : null}
                {isOrderOptionalRequestType(requestType) ? (
                  <div style={{ marginTop: 4 }}>
                    <Button type="primary" block onClick={handleContinueWithoutOrder}>
                      המשך ללא הזמנה (פנייה כללית)
                    </Button>
                    <Text type="secondary" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                      אפשר להמשיך בלי לקשר הזמנה או דוקט — לכל סוגי הפנייה
                    </Text>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Text strong>הזמנות אחרונות</Text>
                {recentLoading ? <Spin size="small" /> : null}
              </div>
              {!recentLoading && recentOrders.length === 0 ? (
                <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                  אין הזמנות אחרונות להצגה
                </Text>
              ) : null}
              {!recentLoading && recentOrders.length > 0 ? (
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  {recentOrders.map((o) => (
                    <Card key={o.key} size="small">
                      <Space direction="vertical" size={4} style={{ width: "100%" }}>
                        <div>
                          <Text type="secondary">מספר הזמנה: </Text>
                          <Text strong dir="ltr">
                            {pickerOrderDisplayNumber(o)}
                          </Text>
                        </div>
                        <div>
                          <Text type="secondary">שם לקוח: </Text>
                          <Text>{o.mainContact || "—"}</Text>
                        </div>
                        <div>
                          <Text type="secondary">מלון / יעד: </Text>
                          <Text>{o.hotelName || "—"}</Text>
                        </div>
                        <div>
                          <Text type="secondary">תאריך: </Text>
                          <Text>
                            {o.checkIn && o.checkOut
                              ? `${o.checkIn} – ${o.checkOut}`
                              : o.checkIn || o.checkOut || "—"}
                          </Text>
                        </div>
                        <div>
                          <Text type="secondary">סטטוס: </Text>
                          <Text>{o.status || "—"}</Text>
                        </div>
                        {o.category ? (
                          <div>
                            <Text type="secondary">קטגוריה: </Text>
                            <Tag>{o.category}</Tag>
                          </div>
                        ) : null}
                        <Button type="primary" block size="small" onClick={() => handleSelectFromRecent(o)}>
                          בחר
                        </Button>
                      </Space>
                    </Card>
                  ))}
                </Space>
              ) : null}
            </div>

            <Divider plain style={{ margin: "4px 0" }}>
              לא מצאת? חפש הזמנה
            </Divider>

            {showSearchIdle ? (
              <Text type="secondary" style={{ display: "block", textAlign: "center" }}>
                חפש הזמנה לפי מספר הזמנה, שם לקוח, יעד או מלון
              </Text>
            ) : null}

            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Input
                  allowClear
                  placeholder="הקלד לחיפוש…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ flex: 1 }}
                />
                {pickSearching ? <Spin size="small" /> : null}
              </div>
            </div>

            {showSearchTooShort ? (
              <Text type="secondary" style={{ display: "block" }}>
                הקלד לפחות {MIN_OPERATION_REQUEST_QUERY_CHARS} תווים
              </Text>
            ) : null}

            {showSearchEmptyResults ? (
              <Text type="secondary" style={{ display: "block", textAlign: "center", padding: "16px 8px" }}>
                לא נמצאו הזמנות
              </Text>
            ) : null}

            {showSearchResults ? (
              <div
                style={{
                  maxHeight: "calc(100vh - 420px)",
                  overflowY: "auto",
                  paddingInlineEnd: 4,
                }}
              >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  {pickResults.map((o) => {
                    const amt = resolveOrderAmountDisplayForPicker(o, {
                      emitTrace: Boolean(import.meta.env?.DEV),
                    });
                    return (
                    <Card
                      key={o.key}
                      size="small"
                      title={
                        <Space wrap size="small">
                          <Text strong dir="ltr">#{pickerOrderDisplayNumber(o)}</Text>
                          <Tag>{o.category}</Tag>
                        </Space>
                      }
                    >
                      <Space direction="vertical" size={4} style={{ width: "100%" }}>
                        <div>
                          <Text type="secondary">לקוח: </Text>
                          <Text>{o.mainContact || "—"}</Text>
                        </div>
                        <div>
                          <Text type="secondary">יעד / מלון: </Text>
                          <Text>{o.hotelName || "—"}</Text>
                        </div>
                        <div>
                          <Text type="secondary">תאריכים: </Text>
                          <Text>
                            {o.checkIn} – {o.checkOut}
                          </Text>
                        </div>
                        <div>
                          <Text type="secondary">סטטוס: </Text>
                          <Text>{o.status || "—"}</Text>
                        </div>
                        {(Number.isFinite(o.totalAmount) && o.totalAmount !== 0) ||
                        (Number.isFinite(o.paidAmount) && o.paidAmount !== 0) ||
                        (Number.isFinite(o.remainingAmount) && o.remainingAmount !== 0) ? (
                          <div>
                            <Text type="secondary">סכום / יתרה: </Text>
                            <Text>
                              סכום: {amt.displayTotalText}
                              {Number.isFinite(o.paidAmount) && o.paidAmount !== 0 ? (
                                <> · שולם {amt.displayPaidText}</>
                              ) : null}
                              {Number.isFinite(o.remainingAmount) ? (
                                <> · יתרה {amt.displayRemainingText}</>
                              ) : null}
                            </Text>
                          </div>
                        ) : null}
                        <Button type="primary" block onClick={() => handleSelectFromSearch(o)}>
                          בחר הזמנה
                        </Button>
                      </Space>
                    </Card>
                    );
                  })}
                </Space>
              </div>
            ) : null}
          </Space>
        ) : null}
      </Space>
    </Drawer>
  );
}
